import { apiGet, apiPost } from './client';

// ============================================================================
// Prompt A/B Test — TypeScript mirrors of PromptAbTestContracts.cs.
// Runs the same prompt at two versions against the same dataset + chat models,
// then returns per-model verdicts and an overall verdict.
// ============================================================================

/** Overall + per-model verdict. Matches Core.Models.PromptAbVerdict ordinals
 *  — keep values stable. Serialized as an int by ASP.NET Core (no
 *  JsonStringEnumConverter is registered in Program.cs — matches
 *  DocumentStatus / BatchStatus / AttackCategory convention). */
export type PromptAbVerdict = 0 | 1 | 2 | 3;
export const PROMPT_AB_VERDICT = {
  InsufficientData: 0 as const,
  AWins: 1 as const,
  BWins: 2 as const,
  Tie: 3 as const,
};

export interface RunPromptAbTestRequest {
  versionAId: string;
  versionBId: string;
  datasetId: string;
  chatModelIds: string[];
  evaluatorName?: string;
}

export interface PromptAbTestPreflightRequest {
  versionAId: string;
  versionBId: string;
  datasetId: string;
  chatModelIds: string[];
}

/** One side of the per-model comparison. Errors count toward errorCount but
 *  are excluded from avgOverallScore + avgLatencyMs (matches DashboardService
 *  + TrendsService convention). */
export interface PromptAbSideDto {
  avgOverallScore: number;
  totalCostUsd: number;
  avgLatencyMs: number;
  sampleCount: number;                  // successful cells only
  errorCount: number;
}

export interface PromptAbTestPerModelDto {
  chatModelId: string;
  providerName: string;
  modelName: string;
  versionA: PromptAbSideDto;
  versionB: PromptAbSideDto;
  verdict: PromptAbVerdict;
  /** deltaScore = versionB.avgOverallScore - versionA.avgOverallScore.
   *  Positive → B improved on A. Negative → B regressed. */
  deltaScore: number;
}

export interface PromptAbTestResponse {
  abTestId: string;
  promptId: string;
  versionAId: string;
  versionBId: string;
  versionANumber: number;
  versionBNumber: number;
  datasetId: string;
  datasetName: string;
  matrixRunAId: string;
  matrixRunBId: string;
  verdict: PromptAbVerdict;
  perModel: PromptAbTestPerModelDto[];
  createdAt: string;
}

export interface PromptAbTestListItem {
  abTestId: string;
  promptId: string;
  versionAId: string;
  versionBId: string;
  versionANumber: number;
  versionBNumber: number;
  verdict: PromptAbVerdict;
  chatModelCount: number;
  createdAt: string;
}

/** Preflight — wraps two matrix preflights, one per side. Matrix preflight is
 *  deterministic on shape, so the two sides carry identical numbers today;
 *  kept as separate fields so per-version cost tweaks (e.g. different
 *  max_tokens) can land without a response-shape change. */
export interface PromptAbTestPreflightResponse {
  promptId: string;
  versionAId: string;
  versionBId: string;
  versionA: MatrixPreflightMinimalResponse;
  versionB: MatrixPreflightMinimalResponse;
  totalEstimatedCostUsd: number;
  totalEstimatedSeconds: number;
}

/** Minimal shape re-exported here so consumers don't have to reach into
 *  matrix.ts. Same fields as MatrixPreflightResponse. */
export interface MatrixPreflightMinimalResponse {
  questionCount: number;
  promptCount: number;
  modelCount: number;
  cellCount: number;
  estimatedCostUsd: number;
  estimatedSeconds: number;
  perModel: {
    chatModelId: string;
    displayLabel: string;
    estimatedCostUsd: number;
  }[];
}

// ============================================================================
// API client functions
// ============================================================================

export const runPromptAbTest = (promptId: string, body: RunPromptAbTestRequest) =>
  apiPost<PromptAbTestResponse>(`/api/prompts/${promptId}/ab-test`, body);

export const preflightPromptAbTest = (promptId: string, body: PromptAbTestPreflightRequest) =>
  apiPost<PromptAbTestPreflightResponse>(`/api/prompts/${promptId}/ab-test/preflight`, body);

export const listPromptAbTests = (promptId: string, limit = 20) =>
  apiGet<PromptAbTestListItem[]>(`/api/prompts/${promptId}/ab-tests?limit=${limit}`);

export const getPromptAbTest = (abTestId: string) =>
  apiGet<PromptAbTestResponse>(`/api/prompts/ab-tests/${abTestId}`);

// ============================================================================
// Phase 5 M3 — promote-winner
// ============================================================================

export interface PromoteWinnerRequest {
  abTestId: string;
  /** Free-form actor label until auth lands. Optional. */
  promotedBy?: string;
}

/** Wire response mirrors PromoteWinnerResponse.cs. `prompt` carries the fully
 *  refreshed PromptDto so the caller can update local state without a second
 *  round-trip. `fromVersionId` is null for the very first promotion. */
export interface PromoteWinnerResponse {
  promotionId: string;
  promptId: string;
  fromVersionId: string | null;
  toVersionId: string;
  toVersionNumber: number;
  abTestId: string;
  promotedBy: string | null;
  promotedAt: string;
  prompt: unknown; // caller re-fetches with getPrompt(); avoid duplicating PromptDto shape here
}

export const promoteWinner = (promptId: string, body: PromoteWinnerRequest) =>
  apiPost<PromoteWinnerResponse>(`/api/prompts/${promptId}/promote-winner`, body);
